import { Button, Dialog, Icon } from '@chakra-ui/react'
import { WalletPaymentStage } from 'commons/components/modal/WalletPaymentStage'
import { VerifyIdentityStage } from 'commons/components/modal/VerifyIdentityStage'
import { createVerifyIdentityValidators } from 'commons/components/modal/verifyIdentityValidation'
import { CountryISO } from 'commons/constants/countries'
import { isXpayType, PaymentTypes } from 'commons/constants/paymentTypes'
import { loadPaymentFlow, verifyIdentity } from 'commons/services/api/paymentApi'
import { FormStep } from 'commons/types/paymentTypes'
import { showIframe } from 'commons/utils/iframeUtils'
import { useMemo, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { MdClose } from 'react-icons/md'

type UnifiedPaymentModalProps = {
	isOpen: boolean
	onClose: () => void
	onProcessingChange?: (processing: boolean) => void
	requestPubUuid: string
	onPaymentCompleted?: () => void
	source: string
	paymentType: PaymentTypes
	amount: number
}

export const UnifiedPaymentModal = ({
	isOpen,
	onClose,
	onProcessingChange,
	requestPubUuid,
	onPaymentCompleted,
	source,
	paymentType,
	amount
}: UnifiedPaymentModalProps) => {
	const { t, i18n } = useTranslation()
	const [step, setStep] = useState<FormStep>(FormStep.STEP_NAME)
	const [firstname, setFirstname] = useState<string>('')
	const [lastname, setLastname] = useState<string>('')
	const [birthdate, setBirthdate] = useState<string>('')
	const [city, setCity] = useState<string>('')
	const [country, setCountry] = useState<string>(CountryISO.FR)
	const [zipcode, setZipcode] = useState<string>('')
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const [errors, setErrors] = useState<{
		firstname?: string
		lastname?: string
		birthdate?: string
		zipcode?: string
		city?: string
		country?: string
		generic?: string
	}>({})
	const [isManualMode, setIsManualMode] = useState<boolean>(false)

	const {
		validateFirstname,
		validateLastname,
		validateZipcode,
		validateCity,
		validateCountry,
		validateBirthdate
	} = useMemo(() => createVerifyIdentityValidators(paymentType, t), [paymentType, t])

	const validateForm = (): boolean => {
		const firstnameError = validateFirstname(firstname)
		const lastnameError = validateLastname(lastname)
		const zipcodeError = validateZipcode(zipcode)
		const cityError = validateCity(city)
		const countryError = validateCountry(country)
		const birthdateError = validateBirthdate(birthdate, step)

		const newErrors: typeof errors = {
			...(firstnameError && { firstname: firstnameError }),
			...(lastnameError && { lastname: lastnameError }),
			...(zipcodeError && { zipcode: zipcodeError }),
			...(cityError && { city: cityError }),
			...(countryError && { country: countryError }),
			...(birthdateError && { birthdate: birthdateError })
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSubmit = async (e: FormEvent) => {
		e.preventDefault()
		setErrors({})

		if (!validateForm()) {
			return
		}

		setIsLoading(true)
		try {
			const response = await verifyIdentity(
				firstname.trim(),
				lastname.trim(),
				requestPubUuid,
				step === FormStep.STEP_BIRTHDATE ? birthdate.trim() : null
			)
			if (response.success) {
				try {
					const paymentFlowResponse = await loadPaymentFlow(
						firstname.trim(),
						lastname.trim(),
						requestPubUuid,
						paymentType,
						i18n.language
					)
					if (paymentType === PaymentTypes.CARD && paymentFlowResponse.success) {
						handleClose()
						showIframe(paymentFlowResponse.url ?? '')
					} else {
						console.error('Failed to load payment session')
						setErrors({
							generic: t('phone_form.verify_identity_error_generic')
						})
					}
				} catch (error) {
					console.error('Error loading payment session:', error)
					setErrors({
						generic: t('phone_form.verify_identity_error_generic')
					})
				}
			} else {
				if (step === FormStep.STEP_NAME) {
					setStep(FormStep.STEP_BIRTHDATE)
				} else {
					setErrors({
						generic: response.message || t('phone_form.verify_identity_error_generic')
					})
				}
			}
		} catch {
			setErrors({
				generic: t('phone_form.verify_identity_error_generic')
			})
		} finally {
			setIsLoading(false)
		}
	}

	const resetForm = () => {
		setStep(FormStep.STEP_NAME)
		setFirstname('')
		setLastname('')
		setBirthdate('')
		setCity('')
		setCountry(CountryISO.FR)
		setZipcode('')
		setErrors({})
		setIsLoading(false)
		setIsManualMode(false)
	}

	const handleClose = () => {
		resetForm()
		onClose()
	}

	if (isXpayType(paymentType)) {
		return isOpen ? (
			<WalletPaymentStage
				requestPubUuid={requestPubUuid}
				paymentType={paymentType}
				amount={amount}
				onProcessingChange={onProcessingChange}
				onClose={handleClose}
				onPaymentCompleted={onPaymentCompleted}
			/>
		) : null
	}

	return (
		<Dialog.Root
			open={isOpen}
			onOpenChange={handleClose}
			size='md'
			placement='center'
			closeOnInteractOutside={false}
			modal={isXpayType(paymentType)}
		>
			<Dialog.Backdrop />
			<Dialog.Positioner>
				<Dialog.Content
					borderRadius='md'
					maxW={{ base: '90vw', lg: '500px' }}
					minW={{ base: 'auto', lg: '500px' }}
				>
					<Dialog.CloseTrigger asChild>
						<Button variant='ghost' size='sm' onClick={handleClose}>
							<Icon as={MdClose} boxSize='20px' />
						</Button>
					</Dialog.CloseTrigger>
					<Dialog.Body p={6}>
						<VerifyIdentityStage
							firstname={firstname}
							lastname={lastname}
							birthdate={birthdate}
							city={city}
							zipcode={zipcode}
							country={country}
							step={step}
							errors={errors}
							isLoading={isLoading}
							onSubmit={handleSubmit}
							onFirstnameChange={setFirstname}
							onLastnameChange={setLastname}
							onBirthdateChange={setBirthdate}
							onCityChange={setCity}
							onCountryChange={setCountry}
							onZipcodeChange={setZipcode}
							onClearError={(field) => {
								setErrors((prev) => ({ ...prev, [field]: undefined }))
							}}
							isManualMode={isManualMode}
							onManualModeChange={setIsManualMode}
							source={source}
							paymentType={paymentType}
						/>
					</Dialog.Body>
				</Dialog.Content>
			</Dialog.Positioner>
		</Dialog.Root>
	)
}
